import { VERSIONED_SORT_LABEL } from '@/lib/versioned-labels';
import type {
  CriterionBandProblem,
  CriterionCard,
  CriterionExamplesFilter,
  CriterionScaleProblem,
  CriterionScoring,
  CriterionScoringFilter,
  CriterionSort,
} from '@matter_extractor/shared';
import {
  CRITERION_SCORING_BANDS_MIN,
  CRITERION_SCORING_TOP,
  assertNever,
  plancherBound,
} from '@matter_extractor/shared';

/**
 * The words that belong to the CRITÈRE and to nothing else (refonte modulaire
 * §6).
 *
 * Everything a screen of the versioning pattern says without knowing what it is
 * looking at - the dates, the two pointers, « utilisé dans », the four common
 * refusals, the draft state - is in `versioned-labels.ts`. What is here is the
 * criterion's own vocabulary: how many examples feed the scribe, and the filter
 * and the sort that count them. The `VersionedWording` the layer is handed - the
 * one that lets it write « Retirer ce critère » without knowing the noun - is
 * beside this file in `criterion-wording.tsx`, because two of its sentences are
 * JSX.
 */

/** « aucun exemple » / « 1 exemple » / « 3 exemples ». */
export function exampleCountLabel(count: number): string {
  if (count === 0) return 'aucun exemple';
  return `${count} ${count === 1 ? 'exemple' : 'exemples'}`;
}

export const EXAMPLES_FILTER_LABEL: Readonly<Record<CriterionExamplesFilter, string>> = {
  all: 'Tous',
  none: 'Aucun',
  few: '1 à 4',
  many: '5 et plus',
};

export const SCORING_FILTER_LABEL: Readonly<Record<CriterionScoringFilter, string>> = {
  all: 'Toutes',
  scored: 'Notés',
  unscored: 'Non notés',
};

/* ---------------------------------------------------------------- notation */

/** « 5 tranches », and « 1 tranche » which no saved scale can be (§6, H44). */
export function bandCountLabel(count: number): string {
  return `${count} ${count === 1 ? 'tranche' : 'tranches'}`;
}

/**
 * The facets of a card (§6), and the rule that shapes the second one: « noté ·
 * 5 tranches » appears ONLY on a scored criterion.
 *
 * « Une absence ne s'affiche pas » (§4, §6). A « non noté » on every other card
 * would be the library saying twice per row what the mention already says by
 * being there - and it would make the one thing worth spotting the quietest.
 */
export function criterionFacets(card: CriterionCard): string[] {
  const facets = [exampleCountLabel(card.exampleCount)];
  // The DEVELOPMENT half, like every other facet of a card: the list shows what
  // the criterion IS. The production half is read by the protocol editor, which
  // asks a different question - what the version a citation points at says.
  const scoring = card.scoring.development;
  if (scoring !== null) facets.push(`noté · ${bandCountLabel(scoring.bandCount)}`);
  return facets;
}

/**
 * The pill of the criterion's fiche in the protocol editor (maquette
 * `CarteEtFiche.dc.html`): « noté · 5 tranches · plancher 50 ».
 *
 * It names the plancher where the card does not, and that is what the fiche is
 * opened for: the line of an objective can pose a threshold on this criterion
 * (§7.2), and the plancher is what applies when it does not.
 */
export function scoringPillLabel(scoring: CriterionScoring): string {
  return `noté · ${bandCountLabel(scoring.bands.length)} · plancher ${plancherBound(scoring)}`;
}

/**
 * What is wrong with ONE band, under its row (maquette `TrancheEnDefaut.dc.html`).
 *
 * Each sentence names the defect AND its consequence on the neighbour, because
 * a contiguous scale has no local defect: a band with no end is also the next
 * band with no beginning, and an operator told only the first repairs the wrong
 * row. The last sentence is the same in all of them - it is the answer to « why
 * is the button grey », which is the question the caption is really under.
 */
export function bandProblemSentence(problem: CriterionBandProblem, bandCount: number): string {
  const number = problem.band + 1;
  const refused = 'La version ne peut pas être enregistrée.';
  switch (problem.kind) {
    case 'missing-upper':
      return problem.band === bandCount - 1
        ? `Borne haute manquante : la tranche ${number} n’a pas de fin. ${refused}`
        : `Borne haute manquante : la tranche ${number} n’a pas de fin, donc la tranche ${number + 1} pas de début. ${refused}`;
    case 'non-increasing-upper':
      return `Borne haute trop basse : la tranche ${number} finirait avant de commencer. Les bornes montent d’une tranche à l’autre. ${refused}`;
    case 'last-not-100':
      return `Dernière tranche : l’échelle finit à ${CRITERION_SCORING_TOP}. ${refused}`;
    case 'empty-text':
      return `Description manquante : la tranche ${number} ne dit pas ce qu’elle couvre. ${refused}`;
    default:
      return assertNever(problem, 'band problem');
  }
}

/**
 * What is wrong with the scale as a WHOLE, under the section rather than under
 * a row (§6, H44).
 *
 * None of the three is reachable from this editor - it floors the list at two
 * bands, sets the plancher from a radio and only offers a passable band below
 * it - but a scoring posted by the assistant of §20 can carry any of them, and a greyed button with no sentence is
 * the failure `scoringProblems` exists to prevent.
 */
export function scaleProblemSentence(problem: CriterionScaleProblem): string {
  switch (problem.kind) {
    case 'too-few-bands':
      return `Une notation compte au moins ${CRITERION_SCORING_BANDS_MIN} tranches. La version ne peut pas être enregistrée.`;
    case 'plancher-out-of-range':
      return 'Le plancher ne désigne aucune tranche. Choisissez la tranche à partir de laquelle le critère est rempli. La version ne peut pas être enregistrée.';
    case 'passable-not-below-plancher':
      return 'La tranche passable doit se trouver sous la tranche plancher. Choisissez-en une plus basse, ou aucune. La version ne peut pas être enregistrée.';
    default:
      return assertNever(problem, 'scale problem');
  }
}

/** The four common sorts of §4, plus the criterion's own (§6). */
export const SORT_LABEL: Readonly<Record<CriterionSort, string>> = {
  ...VERSIONED_SORT_LABEL,
  examples: 'Exemples',
};
